import React, { useEffect, useRef, useState } from 'react';
import '../styles/BoardControls.css';

export type BoardColumn = { key: string; label: string; icon?: React.ReactNode };

const COLOR_OPTIONS = ['Status', 'Health', 'Owner', 'None'];

const colIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="14" y2="17"/></svg>
);

export type BoardPrefs = { colorBy: string; columns: Record<string, boolean> };

// Columns default to visible unless explicitly turned off.
export const isColumnVisible = (columns: Record<string, boolean>, key: string) => columns[key] !== false;

export const loadBoardPrefs = (storageKey: string): BoardPrefs => {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (raw && typeof raw === 'object') return { colorBy: raw.colorBy ?? 'Status', columns: raw.columns ?? {} };
  } catch { /* ignore */ }
  return { colorBy: 'Status', columns: {} };
};

/** Generic board-controls drawer: toggles which list columns are visible for a
 *  page. `columns` is the page's own toggleable column set; visibility persists
 *  per page (the page owns prefs + storage). */
const BoardControlsDrawer: React.FC<{
  columns: BoardColumn[];
  prefs: BoardPrefs;
  onChange: (prefs: BoardPrefs) => void;
  onClose: () => void;
}> = ({ columns, prefs, onChange, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [screen, setScreen] = useState<'main' | 'columns'>('main');
  const [colorMenuOpen, setColorMenuOpen] = useState(false);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const visibleCount = columns.filter(c => isColumnVisible(prefs.columns, c.key)).length;
  const toggleCol = (key: string) => onChange({ ...prefs, columns: { ...prefs.columns, [key]: !isColumnVisible(prefs.columns, key) } });

  return (
    <div className="bc-drawer" ref={ref} onClick={e => e.stopPropagation()}>
      {screen === 'main' ? (
        <>
          <div className="bc-section-label">Card customization</div>
          <div className="bc-row" style={{ position: 'relative' }} onClick={() => setColorMenuOpen(o => !o)}>
            <span className="bc-row-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="13.5" cy="6.5" r="1.5"/><circle cx="17.5" cy="10.5" r="1.5"/><circle cx="8.5" cy="7.5" r="1.5"/><circle cx="6.5" cy="12.5" r="1.5"/><path d="M12 2a10 10 0 1 0 0 20c1 0 1.5-.8 1.5-1.5 0-1.5 1-2 2-2H18a4 4 0 0 0 4-4 10 10 0 0 0-10-10z"/></svg></span>
            <span className="bc-row-label">Attributes</span>
            <span className="bc-row-value">{visibleCount}</span>
            <svg className="bc-row-chev" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            {colorMenuOpen && (
              <div className="bc-color-menu" onClick={e => e.stopPropagation()}>
                {COLOR_OPTIONS.map(o => (
                  <button key={o} type="button" className={`bc-color-option ${prefs.colorBy === o ? 'selected' : ''}`} onClick={() => { onChange({ ...prefs, colorBy: o }); setColorMenuOpen(false); }}>
                    {o}
                    {prefs.colorBy === o && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bc-section-label">Columns</div>
          <button type="button" className="bc-row bc-row-btn" onClick={() => setScreen('columns')}>
            <span className="bc-row-checkbox">{visibleCount > 0 && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}</span>
            <span className="bc-row-label">Attributes</span>
            <span className="bc-row-value">{visibleCount > 0 ? visibleCount : 'None'}</span>
            <svg className="bc-row-chev" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </>
      ) : (
        <>
          <button type="button" className="bc-back" onClick={() => setScreen('main')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            <span>Columns</span>
          </button>
          <div className="bc-attr-list">
            {columns.map(a => (
              <div key={a.key} className="bc-attr-row">
                <span className="bc-row-icon">{a.icon ?? colIcon}</span>
                <span className="bc-row-label">{a.label}</span>
                <button type="button" className={`bc-toggle ${isColumnVisible(prefs.columns, a.key) ? 'on' : ''}`} onClick={() => toggleCol(a.key)} aria-pressed={isColumnVisible(prefs.columns, a.key)}>
                  <span className="bc-toggle-knob" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BoardControlsDrawer;
